import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API_URL } from "../api";
import { COLOR, FONT, RADIUS, SHADOW, EASE, EASE_SPRING } from "./mobiletheme";
import { getImageUrl } from "../utils/imageUrl";

export default function MobileBottomNav({ activeTab, setActiveTab, onlyCart = false, transparent = false, viewCartBar = false, hideLang = false }) {
  const [cartCount, setCartCount] = useState(0);
  const [cartSummary, setCartSummary] = useState({ totalQty: 0, thumbnails: [] });
  const navigate = useNavigate();
  const location = useLocation();

  // Language Modal State & Hook
  const [showLangModal, setShowLangModal] = useState(false);
  const { t, i18n } = useTranslation();

  const getUserId = () => {
    try { return localStorage.getItem("phone") || "guest"; } catch (_) { return "guest"; }
  };

  const isMountedRef = useRef(true);

  const fetchCartCount = useCallback(async () => {
    const user_id = getUserId();
    if (user_id === "guest") {
      if (isMountedRef.current) {
        setCartCount(0);
        setCartSummary({ totalQty: 0, thumbnails: [] });
      }
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/cart/${user_id}`);
      if (!isMountedRef.current) return;
      if (res.data.success) {
        const cart = res.data.cart;
        const uniqueItems = [...new Set(cart.map((item) => item.product_id))];
        setCartCount(uniqueItems.length);

        if (viewCartBar) {
          const totalQty = cart.reduce((sum, item) => sum + (item.quantity || 0), 0);
          const thumbnails = cart
            .map((item) => item.image || item.product_image)
            .filter(Boolean)
            .slice(0, 2)
            .map((img) => getImageUrl(img));
          setCartSummary({ totalQty, thumbnails });
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        setCartCount(0);
        setCartSummary({ totalQty: 0, thumbnails: [] });
      }
    }
  }, [viewCartBar]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchCartCount();
    window.addEventListener("cartUpdated", fetchCartCount);
    return () => {
      isMountedRef.current = false;
      window.removeEventListener("cartUpdated", fetchCartCount);
    };
  }, [fetchCartCount]);

  const handleTabClick = (tab) => {
    if (tab === "lang") {
      setShowLangModal(true);
      return;
    }

    if (location.pathname === "/" || location.pathname === "/home") {
      if (setActiveTab) setActiveTab(tab);
    } else {
      navigate("/", { state: { targetTab: tab } });
    }
  };

  // UPDATED: Syncs language globally across the entire app
  const changeLanguage = async (lng) => {
    try {
      localStorage.setItem("language", lng);
      await i18n.changeLanguage(lng);
      window.dispatchEvent(new Event("languageChanged"));
      setShowLangModal(false);
    } catch (error) {
      console.error("Language change error:", error);
    }
  };

  const currentHighlight = (location.pathname !== "/" && location.pathname !== "/home") ? "home" : activeTab;

  const HomeIcon = ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
      <polyline points="9 22 9 12 15 12 15 22"></polyline>
    </svg>
  );

  const CartIcon = ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="21" r="1"></circle>
      <circle cx="20" cy="21" r="1"></circle>
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
    </svg>
  );

  const GlobeIcon = ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="2" y1="12" x2="22" y2="12"></line>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
    </svg>
  );

  const UserIcon = ({ color }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
      <circle cx="12" cy="7" r="4"></circle>
    </svg>
  );

  const badgeText = cartCount > 9 ? "9+" : String(cartCount);

  if (viewCartBar) {
    if (cartSummary.totalQty <= 0) return null;
    return (
      <div style={styles.viewCartWrap}>
        <style>{`
          @keyframes sb-cartBarIn { from { opacity: 0; transform: translateY(16px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
          .sb-view-cart-bar { animation: sb-cartBarIn 0.32s ${EASE_SPRING} both; }
          .sb-view-cart-bar:active { transform: scale(0.98); }
        `}</style>
        <div
          style={styles.viewCartBar}
          className="sb-tap sb-view-cart-bar"
          onClick={() => navigate("/", { state: { targetTab: "cart" } })}
          role="button"
          tabIndex={0}
        >
          <div style={styles.viewCartThumbs}>
            {cartSummary.thumbnails.length > 0 ? (
              cartSummary.thumbnails.map((src, idx) => (
                <img
                  key={idx}
                  src={src}
                  alt=""
                  style={{ ...styles.viewCartThumbImg, marginLeft: idx === 0 ? 0 : "-14px", zIndex: cartSummary.thumbnails.length - idx }}
                />
              ))
            ) : (
              <div style={styles.viewCartThumbFallback} />
            )}
          </div>
          <div style={styles.viewCartTextBlock}>
            <span style={styles.viewCartTitle}>{t("nav.viewCart", "View cart")}</span>
            <span style={styles.viewCartSubtitle}>{cartSummary.totalQty} {t("nav.items", "items")}</span>
          </div>
          <div style={styles.viewCartArrowCircle}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLOR.onPrimary} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </div>
        </div>
      </div>
    );
  }

  const tabs = onlyCart
    ? [ { key: "cart", label: t("nav.cart", "Cart"), Icon: CartIcon, badge: cartCount > 0 ? badgeText : null } ]
    : [
        { key: "home", label: t("nav.home", "Home"), Icon: HomeIcon },
        { key: "cart", label: t("nav.cart", "Cart"), Icon: CartIcon, badge: cartCount > 0 ? badgeText : null },
        ...(hideLang ? [] : [{ key: "lang", label: t("nav.lang", "Lang"), Icon: GlobeIcon }]),
        { key: "profile", label: t("nav.profile", "Profile"), Icon: UserIcon },
      ];

  const currentLanguage = i18n.resolvedLanguage || i18n.language;

  return (
    <>
      <div style={styles.navWrapper}>
        <style>{`
          @keyframes sb-badgePop { 0% { transform: scale(0.5); opacity: 0; } 60% { transform: scale(1.15); opacity: 1; } 100% { transform: scale(1); } }
          .sb-nav-item { transition: background-color 0.25s ${EASE}, box-shadow 0.32s ${EASE_SPRING}, padding 0.32s ${EASE_SPRING}, gap 0.32s ${EASE_SPRING}, width 0.32s ${EASE_SPRING}, color 0.25s ${EASE}; }
          .sb-nav-item:active { transform: scale(0.94); }
          .sb-nav-label-in { animation: sb-pop 0.28s ${EASE_SPRING} both; }
          .sb-badge-pop { animation: sb-badgePop 0.3s ${EASE_SPRING} both; }
        `}</style>
        <div style={transparent ? styles.staticBottomNavTransparent : styles.staticBottomNav}>
          {tabs.map(({ key, label, Icon, badge }) => {
            const active = currentHighlight === key;
            return (
              <div
                key={key}
                style={active ? styles.navItemActive : styles.navItem}
                className="sb-tap sb-nav-item"
                onClick={() => handleTabClick(key)}
              >
                <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon color={active ? COLOR.paper : COLOR.muted} />
                  {badge && <span key={badge} style={styles.badge} className="sb-badge-pop">{badge}</span>}
                </div>
                {active && <span style={styles.navLabel} className="sb-nav-label-in">{label}</span>}
              </div>
            );
          })}
        </div>
      </div>

      {showLangModal && (
        <div style={styles.langOverlay} onClick={() => setShowLangModal(false)}>
          <div style={styles.langModal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.langTitle}> {t("mobile.selectLanguage", "Select Language")} </h3>
            
            <button style={styles.langBtn} className="sb-tap" onClick={() => changeLanguage('en')}>
              <span style={styles.langText}>English</span>
              {currentLanguage === 'en' && <span style={styles.checkIcon}>✓</span>}
            </button>
            
            <button style={styles.langBtn} className="sb-tap" onClick={() => changeLanguage('te')}>
              <span style={styles.langText}>తెలుగు (Telugu)</span>
              {currentLanguage === 'te' && <span style={styles.checkIcon}>✓</span>}
            </button>
            
            <button style={styles.langBtn} className="sb-tap" onClick={() => changeLanguage('hi')}>
              <span style={styles.langText}>हिंदी (Hindi)</span>
              {currentLanguage === 'hi' && <span style={styles.checkIcon}>✓</span>}
            </button>
            
            <button style={styles.cancelBtn} className="sb-tap" onClick={() => setShowLangModal(false)}> 
              {t("mobile.cancel", "Cancel")} 
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export const NAV_HEIGHT = 80;

const styles = {
  navWrapper: { position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 9999, display: "flex", justifyContent: "center", paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))", paddingTop: "10px", pointerEvents: "none", backgroundColor: "transparent" },
  staticBottomNav: { pointerEvents: "auto", display: "flex", alignItems: "center", gap: "8px", backgroundColor: COLOR.paper, backdropFilter: "blur(18px) saturate(180%)", WebkitBackdropFilter: "blur(18px) saturate(180%)", padding: "8px", borderRadius: RADIUS.pill, boxShadow: "0 10px 24px rgba(28,36,51,0.12)", border: `1px solid ${COLOR.line}` },
  staticBottomNavTransparent: { pointerEvents: "auto", display: "flex", alignItems: "center", gap: "8px", backgroundColor: "transparent", padding: "8px", borderRadius: RADIUS.pill, boxShadow: "none", border: "none" },
  navItem: { display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", color: COLOR.muted, cursor: "pointer", width: "54px", height: "54px", borderRadius: RADIUS.pill },
  navItemActive: { display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "8px", color: COLOR.paper, cursor: "pointer", height: "54px", padding: "0 20px 0 16px", borderRadius: RADIUS.pill, background: COLOR.gradientCTA, boxShadow: SHADOW.cta },
  navLabel: { fontFamily: FONT.body, fontSize: "14px", fontWeight: 700, whiteSpace: "nowrap" },
  badge: { position: "absolute", top: "-8px", right: "-12px", backgroundColor: COLOR.pop, color: COLOR.onPrimary, fontSize: "10px", fontWeight: "800", padding: "2px 6px", borderRadius: "10px", border: `2px solid ${COLOR.paper}`, minWidth: "14px", textAlign: "center", lineHeight: "14px" },

  viewCartWrap: { position: "fixed", left: "16px", right: "16px", bottom: "calc(16px + env(safe-area-inset-bottom, 0px))", zIndex: 9999, display: "flex", justifyContent: "center" },
  viewCartBar: { width: "100%", maxWidth: "480px", display: "flex", alignItems: "center", gap: "12px", padding: "8px 8px 8px 12px", borderRadius: "12px", background: COLOR.primary, boxShadow: "0 4px 12px rgba(255,51,102,0.3), inset 0 1px 0 rgba(255,255,255,0.18)", cursor: "pointer", boxSizing: "border-box" },
  viewCartThumbs: { display: "flex", alignItems: "center", flexShrink: 0 },
  viewCartThumbImg: { width: "42px", height: "42px", borderRadius: "8px", objectFit: "cover", border: "2px solid rgba(255,255,255,0.85)", backgroundColor: COLOR.surface },
  viewCartThumbFallback: { width: "42px", height: "42px", borderRadius: "8px", border: "2px solid rgba(255,255,255,0.85)", backgroundColor: "rgba(255,255,255,0.25)" },
  viewCartTextBlock: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", color: COLOR.onPrimary },
  viewCartTitle: { fontFamily: FONT.body, fontSize: "15px", fontWeight: "800", lineHeight: 1.25 },
  viewCartSubtitle: { fontFamily: FONT.body, fontSize: "12px", fontWeight: "500", color: "rgba(255,255,255,0.85)", lineHeight: 1.3 },
  viewCartArrowCircle: { width: "36px", height: "36px", borderRadius: "8px", backgroundColor: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },

  // Language Modal Styles
  langOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(15, 23, 42, 0.5)", zIndex: 10000, display: "flex", alignItems: "flex-end", animation: `sb-fadeSlide 0.2s ${EASE}` },
  langModal: { width: "100%", backgroundColor: COLOR.paper, borderTopLeftRadius: RADIUS.xl, borderTopRightRadius: RADIUS.xl, padding: "24px 20px 30px", display: "flex", flexDirection: "column", gap: "12px", boxShadow: "0 -8px 30px rgba(0,0,0,0.15)", animation: `sb-voiceUp 0.3s ${EASE_SPRING}` },
  langTitle: { margin: "0 0 10px 0", fontSize: "18px", fontWeight: "700", fontFamily: FONT.display, color: COLOR.ink, textAlign: "center" },
  langBtn: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderRadius: RADIUS.md, border: `1px solid ${COLOR.line}`, backgroundColor: COLOR.mist, cursor: "pointer", fontFamily: FONT.body },
  langText: { fontSize: "15px", fontWeight: "600", color: COLOR.ink },
  checkIcon: { color: COLOR.primaryDark, fontSize: "16px", fontWeight: "bold" },
  cancelBtn: { marginTop: "10px", padding: "14px", borderRadius: RADIUS.md, border: "none", backgroundColor: "transparent", color: COLOR.muted, fontWeight: "700", fontSize: "15px", cursor: "pointer", fontFamily: FONT.body },
};